import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList
} from 'recharts';
import costService from '../services/cost.service';

interface CostBarChartProps {
  data: Array<{
    name: string;
    value: number;
  }>;
  title: string;
  color?: string;
  height?: number;
}

const CostBarChart: React.FC<CostBarChartProps> = ({ 
  data, 
  title, 
  color = '#0088FE',
  height = 275 // Aumentado em 10% (250 * 1.1 = 275)
}) => {
  if (!data || data.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, height: height }}>
        <Typography variant="subtitle1" align="center" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80%' }}>
          <Typography variant="body2" color="text.secondary">
            Não há dados disponíveis para exibição.
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Cores alternativas para as barras
  const colors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, height: height }}>
      <Typography variant="subtitle1" align="center" gutterBottom>
        {title}
      </Typography>
      <Box sx={{ width: '100%', height: 'calc(100% - 40px)' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 20, right: 10, left: 10, bottom: 40 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis 
              dataKey="name" 
              angle={-45} 
              textAnchor="end" 
              height={60} 
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              tickFormatter={(value: number) => costService.formatCurrency(value).replace('R$', '')}
              tick={{ fontSize: 12 }}
            />
            <Tooltip 
              formatter={(value: number) => [costService.formatCurrency(value), 'Valor']}
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '8px'
              }}
            />
            <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
              <LabelList 
                dataKey="value" 
                position="top" 
                formatter={(value: number) => {
                  // Formatar para mostrar apenas os valores principais, sem centavos
                  const formatted = costService.formatCurrency(value);
                  return formatted.replace('R$', '').replace(',00', '');
                }}
                style={{ 
                  fontSize: '11px', 
                  fontWeight: 'bold',
                  fill: '#555',
                  textShadow: '0px 0px 2px #fff'
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

export default CostBarChart;
